import {
  getPasswordPolicy,
  changePasswordWithCode,
  PasswordPolicy,
} from "@/api/openapi";
import { PspContext } from "@/config/contexts";
import { TranslatorKeys, translate } from "@/config/translator";
import { PasswordPolicyError } from "@/config/types";
import { validatePassword } from "@/config/utils";
import { useContext, useRef, useState, KeyboardEvent, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

type UseNewPasswordParams = {
  email: string;
  code: string;
};

export function useNewPassword({ email, code }: UseNewPasswordParams) {
  const { lang } = useContext(PspContext);
  const navigate = useNavigate();

  const confirmPasswordInputRef = useRef<HTMLInputElement>(null);
  const changePasswordButtonRef = useRef<HTMLButtonElement>(null);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState<TranslatorKeys>();
  const [passwordRequirements, setPasswordRequirements] = useState<
    Record<PasswordPolicyError, boolean>
  >({
    missing_digit: true,
    missing_uppercase_letter: true,
    missing_lowercase_letter: true,
    too_short: true,
    missing_symbol: true,
  });
  const [passwordPolicy, setPasswordPolicy] = useState<PasswordPolicy>();
  useEffect(() => {
    getPasswordPolicy().then(({ data, error }) => {
      if (error) {
        toast(translate("somethingWentWrong", lang));
      } else {
        setPasswordPolicy(data);
      }
    });
  }, [lang]);

  const invalidPassword =
    passwordRequirements.missing_digit ||
    passwordRequirements.missing_uppercase_letter ||
    passwordRequirements.missing_symbol ||
    passwordRequirements.missing_lowercase_letter ||
    passwordRequirements.too_short ||
    password !== confirmPassword;

  function onKeyDownPassword(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      confirmPasswordInputRef.current?.focus();
    } else {
      setErrorMessage(undefined);
    }
  }

  function onKeyDownConfirmPassword(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      changePasswordButtonRef.current?.click();
    } else {
      setErrorMessage(undefined);
    }
  }

  function onChangePassword(value: string) {
    if (!passwordPolicy) return;
    setPasswordRequirements(validatePassword(value, passwordPolicy));
    setPassword(value);
  }

  function onSubmitNewPassword() {
    if (password !== confirmPassword) {
      setErrorMessage("passwordsDontMatch");
      return;
    }
    if (password.trim().length === 0) {
      setErrorMessage("passwordCannotBeBlank");
      return;
    }

    getPasswordPolicy().then(({ data, error }) => {
      if (error) {
        toast(translate("somethingWentWrong", lang));
      } else {
        const policyError = validatePassword(password, data);
        setPasswordRequirements(policyError);
        for (const error of Object.values(policyError)) {
          if (error) {
            throw new Error("unsatisfied password requirement");
          }
        }

        changePasswordWithCode({ body: { code, email, password } }).then(
          ({ error }) => {
            if (error) {
              toast(translate("somethingWentWrong", lang));
            } else {
              navigate("/login");
              toast(translate("passwordChangedSuccessfully", lang));
            }
          },
        );
      }
    });
  }

  return {
    onKeyDownConfirmPassword,
    onKeyDownPassword,
    onSubmitNewPassword,
    setConfirmPassword,
    errorMessage,
    passwordRequirements,
    password,
    confirmPassword,
    lang,
    confirmPasswordInputRef,
    changePasswordButtonRef,
    onChangePassword,
    invalidPassword,
  };
}
